import React, { useState, useRef, useEffect } from 'react';
import PasswordTooltip from '../../../../../../../Utils/PasswordToolTip/PasswordToolTip';
import { isValidPassword } from '../../../../../../../Utils/Validations/validatePassword';
import { showErrorToast, showSuccessToast } from '../../../../../../../Context/Toaster/Toaster';
import apiClient from '../../../../../../../Config/apiClient';
import { FIELD_MAX_LENGTHS } from '../../../../Utils/Constants';
import Loader from "../../../../../../../Context/Loader/Loader";

const siteUserPath = process.env.REACT_APP_HS_SITE_USERS_BASE_PATH;

const PasswordTab = ({ userId, setIsEditMode, setActiveTab }) => {
    const [form, setForm] = useState({
        newPassword: '',
        confirmPassword: ''
    });

    const [errors, setErrors] = useState({});
    const [showPasswords, setShowPasswords] = useState({
        newPassword: false,
        confirmPassword: false
    });

    const [isFocused, setIsFocused] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsFocused(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    const toggleVisibility = (field) => {
        setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        const newErrors = {};
        const { newPassword, confirmPassword } = form;

        if (!newPassword) {
            newErrors.newPassword = 'New password is required.';
        } else {
            const pwdValid = isValidPassword(newPassword);
            if (!pwdValid.valid) newErrors.newPassword = pwdValid.message;
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Confirm password is required.';
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChangePassword = async () => {
        if (!validateForm()) return;

        try {
            setIsLoading(true);
            const payload = {
                user_id: userId,
                newPassword: form.newPassword,
                confirmPassword: form.confirmPassword
            };
            const response = await apiClient.put(`${siteUserPath}/change-password`, payload);
            if (response.data.status === 'SUCCESS') {
                showSuccessToast('Password changed successfully');
                setForm({ newPassword: '', confirmPassword: '' });
                setErrors({});
                setIsEditMode(false);
                setActiveTab('info');
            } else {
                showErrorToast(response.data.message || 'Password update failed');
            }
        } catch (err) {
            const msg = err?.response?.data?.message || 'Error updating password';
            showErrorToast(msg);
        } finally {
            setIsLoading(false);
        }
    };

    const fields = [
        { label: 'New Password', name: 'newPassword' },
        { label: 'Confirm Password', name: 'confirmPassword' },
    ];

    return (
        <div className="aptab-inner">
            {fields.map(({ label, name }) => (
                <div className="cp-split" key={name} ref={name === 'newPassword' ? wrapperRef : null} style={{ position: 'relative' }}>
                    <label className="fi-label">
                        {label} <span>*</span>
                    </label>
                    <div className={`input-wraper pass-input ${name === 'newPassword' && isFocused ? 'focused' : ''}`} style={{ position: 'relative' }}>
                        {name === 'newPassword' && isFocused && !form[name] && (
                            <PasswordTooltip />
                        )}
                        <input className={`common-input ${errors[name] ? 'error' : ''}`} type={showPasswords[name] ? 'text' : 'password'} name={name} value={form[name]} onChange={handleChange} onFocus={() => name === 'newPassword' && setIsFocused(true)} maxLength={FIELD_MAX_LENGTHS.password} />
                        <i className={`fa field-icon ${showPasswords[name] ? 'fa-eye' : 'fa-eye-slash'}`} onClick={() => toggleVisibility(name)} style={{ position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)', cursor: 'pointer' }} />
                    </div>
                    <span className={`error-text ${errors[name] ? 'active' : ''}`}>
                        {errors[name] || ' '}
                    </span>
                </div>
            ))}

            <div className="cp-button-group">
                <button className="update-button" onClick={handleChangePassword} disabled={isLoading} >
                    {isLoading ? <Loader /> : 'Update'}
                </button>
                <button className="cancel-button me-4 float-end" onClick={() => { setForm({ newPassword: '', confirmPassword: '' }); setErrors({}); setIsEditMode(false); setActiveTab('info'); }}>
                    Cancel
                </button>
            </div>
        </div>
    );
};

export default PasswordTab;
